import type { Fraction } from '@mimir/common/util/fractionType';
import { getModelTrackBoxDuration, type ModelVideoTrack } from '../timelineModel';
import { addFractions, divideFractionBy, lessThanFraction } from '@mimir/common/util/integerMath';


/**
 * Given a main track, a box current index and a position in seconds,
 * the function returns the index where the box should be
 * inserted after it was removed from its current position.
 *
 * Let's consider the following track:
 *                                          P
 *                                          v
 * +------------------+------------------+
 * |  box A at pos 0  |  box B at pos 1  |
 * +------------------+------------------+
 *
 * For instance, if we want to move box A to position P, the returned
 * index would be 1, since after the removal of A, B would now be at the
 * position 0.
 */
export function findMoveTargetIndex(
  mainTrack: ModelVideoTrack,
  currentIndex: number,
  posInSeconds: Fraction,
): number {
  return findMoveOrInsertTargetIndex(mainTrack, currentIndex, 1, posInSeconds);
}


/**
 * Same as findMoveTargetIndex but for moving a group of contiguous boxes.
 */
export function findMoveTargetIndexForBoxGroup(
  mainTrack: ModelVideoTrack,
  currentIndexOfFirstBox: number,
  nBoxes: number,
  posInSeconds: Fraction,
): number {
  return findMoveOrInsertTargetIndex(mainTrack, currentIndexOfFirstBox, nBoxes, posInSeconds);
}


/**
 * Given a main track and a position in seconds,
 * the function returns the index where a new box should
 * be inserted.
 *
 * Let's consider the following track:
 *                         P
 *                         v
 * +------------------+------------------+
 * |  box A at pos 0  |  box B at pos 1  |
 * +------------------+------------------+
 *
 * For instance, if we want to insert a box Z at position P, the returned
 * index would be 1, since after the insertion the timeline would be A Z B
 */
export function findInsertTargetIndex(
  mainTrack: ModelVideoTrack,
  posInSeconds: Fraction,
): number {
  return findMoveOrInsertTargetIndex(mainTrack, null, 0, posInSeconds);
}


function findMoveOrInsertTargetIndex(
  mainTrack: ModelVideoTrack,
  currentIndexOfFirstBox: number | null,
  nBoxes: number,
  posInSeconds: Fraction,
): number {
  let currentBoxStart: Fraction = { numerator: 0, denominator: 1 };
  let targetIndexWithoutRemoval: number | undefined;

  for (let index = 0; index < mainTrack.length; index++) {
    const box = mainTrack[index];

    const boxDuration = getModelTrackBoxDuration(box);
    const middleOfBox = addFractions(currentBoxStart, (divideFractionBy(boxDuration, 2)));
    const endOfBox = addFractions(currentBoxStart, boxDuration);

    if (lessThanFraction(posInSeconds, middleOfBox)) {
      // If the position is before the middle of the box, then
      // the target position is this box's index, unless the current
      // box is one of the moved box, in which case the target index is
      // the index of the leftmost box of the group
      if (currentIndexOfFirstBox !== null && (index >= currentIndexOfFirstBox && index < (currentIndexOfFirstBox + nBoxes))) {
        targetIndexWithoutRemoval = currentIndexOfFirstBox;
      } else {
        targetIndexWithoutRemoval = index;
      }
      break;
    }

    if (lessThanFraction(posInSeconds, endOfBox)) {
      // If the position is in the second half of the box

      if (currentIndexOfFirstBox !== null && (index >= currentIndexOfFirstBox && index < (currentIndexOfFirstBox + nBoxes))) {
        // If the position is on one of the moved boxes,
        // we keep the current index
        targetIndexWithoutRemoval = currentIndexOfFirstBox;
        break;
      } else {
        // Otherwise, the target index is on the right of the current box
        targetIndexWithoutRemoval = index + 1;
        break;
      }
    }

    currentBoxStart = endOfBox;
  }

  // The target position is at the end of the array
  targetIndexWithoutRemoval ??= mainTrack.length;

  // If there is no source box to move, we are done.
  // If we have source boxes, we now know what the target
  // index should be without taking the box removal into account,
  // so we need to adjust the value if the target position is on
  // the right of the source position
  return (currentIndexOfFirstBox === null || targetIndexWithoutRemoval <= currentIndexOfFirstBox) ? targetIndexWithoutRemoval : targetIndexWithoutRemoval - nBoxes;
}
